/* eslint-disable no-throw-literal */
// Class that represent the BIMSearch engine
// Connected to a specific BIM360 view
// Allow running filters and creating schedules on elements in the view

import i18next from 'i18next';
import { PropertiesManager } from "./PropertiesManager.js";
import { Template, BimsearchElement, modelIdToKey, Collection } from "./TemplatesAndCollections";
import { DataForMenus, QuickDataField, operatorValues, MenusSelection, DataForTable, QuickInfo, valueTypes, PropertyInfo, ColumnInfo, RowInfo, CustomParameter, DateRange, ExecutionReport, BudgetItemData, PRO_PARAM_NAMES, QBS_Item_Unit_Values } from "./ResultClasses";
import { logError } from "./IAmecLog";
import { getFromServer, postToServer } from "./CommonFunctions.js";
import { parseConditionHelper } from "utils/parseConditionHelper";
import { returnUniqueStrings, createCollection } from './IScheduleLogic.js';
// Main class for BIMSearch filter and data search
export class BimSearch {
    // public:
    constructor() {
        this.init();
    }

    init() {
        this.selectedCategoryIds = [];
        this.selectedCategories = [];
        this.categoryIdNameDictionary = {};
        this.sharedPropertiesDictionary = {};
        this.allElements = [];
        this.propertiesManager = new PropertiesManager();
        this.modelName = "";
        this.selectedFilter = "Not selected";
        this.categoryElements = null;
        this.filteredElements = null;
        this.commonCategories = [...CommonCategories];
        this.commonProperties = [...CommonProperties];
        this.commonGroupingOptions = [...CommonGroupingOptions];
        this.nonSummableProperties = null;
        this.userTemplates = null; // Dictionary string --> [] of BimsearchTemplate
        this.customParametersTask = null;
        this.customParametersObject = null;
        this.modelUrl = null;
        this.isTranslateModelEnabled = false
        this.executionReports = null // ExecutionReport instance
    }

    clearTree() {
        this.allElements = [];
        this.propertiesManager = new PropertiesManager();
        this.selectedFilter = "Not selected";
        this.categoryElements = null;
        this.filteredElements = null;
    }

    async loadModel(viewer, objectsTree, modelName) {
        this.clearTree();

        this.modelName = modelName;
        this.viewer = viewer;

        objectsTree.enumNodeChildren(
            objectsTree.getRootId(),
            (nodeId) => {
                this.allElements.push(nodeId);
            },
            true
        );

        await this.propertiesManager.loadElements(viewer, this.allElements);
        await this.propertiesManager.loadLevels(viewer, objectsTree.getRootId());

        this.updatePropertiesTranslations();

        var categories = [];
        var categoryIds = [];
        var templatesCount = [];
        var templates = [];
        var templatesFolder = new Object();
        var x = 1;
        this.userTemplates = await getUserTemplates();

        Object.keys(this.userTemplates).forEach(key => {
            this.userTemplates[key].sort((a, b) => a.title < b.title ? -1 : 1);
            templatesFolder[key] = [];
            this.userTemplates[key].forEach(temp => {
                templatesFolder[key].push(temp.title);
                templates.push(temp.title);
                templatesCount.push(0);
            });
        });

        for (var i = 0; i < this.allElements.length; i++) {
            var props = this.propertiesManager.GetProperties(this.allElements[i]);
            if (props == null) continue;

            var category = this.findByDisplayName(props, CategoryParameterName);

            var categoryId = this.findByDisplayName(props, "CategoryId");

            if (category) {
                var categoryName = category.displayValue;
                if (categoryName.startsWith(categoryPre))
                    categoryName = categoryName.slice(categoryPre.length);
                if (!categories.includes(categoryName)) {
                    categories.push(categoryName);

                    if (categoryId != undefined) {
                        if (!(categoryId.displayValue in this.categoryIdNameDictionary)) {
                            this.categoryIdNameDictionary[categoryId.displayValue] = categoryName;
                            categoryIds.push(categoryId);
                        }
                    }
                }

            }
        }

        this.updateCategoriesTranslations();

        var res;

        if (categories.length == 0) {
            res = await this.selectCategory(null, null);
        } else {
            categories.sort();
            res = new DataForMenus();
        }

        res.templates = templates;
        res.templatesCount = templatesCount;
        res.templatesFolder = templatesFolder;

        res.categories = categories;
        this.splitCommon(res);

        this.customParametersObject = await this.customParametersTask;

        if (this.isTranslateModelEnabled && res.categories && res.categories.length > 0) {
            const translations = await this.handleHebrewTranslation(res.categories)
            res.translations = translations
        }

        return res;
    }

    async loadCustomParameter(modelId) {
        this.modelUrl = modelId;
        var url = `/api/forge/customparameters/model?model=${modelId}`;
        this.customParametersTask = getFromServer(url);
    }

    /**
     * High level function to control the process of a translating English expressions to Hebrew.
     *
     * @param {Array<string>} expressions - list of english expressions
     * @async
     * @returns {Array<string>} A promise that resolves into An array of objects formatted for frontend usage.
     *                  { id: "apple", hebrewTranslation: "תפוח" }
     */
    async handleHebrewTranslation(expressions) {
        try {
            const result = await postToServer("/api/hebrewTranslations", { expressions: expressions })
            const resultJson = await result.json()

            return resultJson.data.expressions            
        } catch (err) {
            return []
        }
    }
    async selectCategory(categories, categoryIds) {
        if (categoryIds == null) {
            categoryIds = [];
        }

        this.selectedCategories = categories;
        this.selectedCategoryIds = categoryIds;

        this.selectedFilter = "Not selected";

        var res = new DataForMenus();
        this.categoryElements = await this.findElementsByCategories(categories, categoryIds);

        var sharedProperties = this.getSharedPropertiesByDisplayName(this.categoryElements);

        this.filteredElements = this.categoryElements;

        var counterData = this.getCounterData(this.categoryElements);

        res.quickCounterData = counterData;
        res.filterByOptions = sharedProperties;
        res.properties = sharedProperties;
        res.groupingOptions = sharedProperties;
        res.filterValues = [];
        res.customProperties = Object.keys(this.customParametersObject.parameters);
        this.splitCommon(res);

        let translations
        if (this.isTranslateModelEnabled && res.properties?.length > 0) {
            translations = await this.handleHebrewTranslation (res.properties)
            res.translations = translations
        }

        return res;
    }

    async findElementsByCategories(categories, categoryIds) {
        var res = [];

        if (categories == null) {
            res = this.allElements;
        } else if (categories.length > 0) {
            for (var i = 0; i < this.allElements.length; i++) {
                var props = this.propertiesManager.GetProperties(this.allElements[i]);

                var categoryPropIds = this.findByDisplayName(props, "CategoryId");
                var categoryPropNames = this.findByDisplayName(props, CategoryParameterName);

                var elementDetected = false;

                if (categoryPropIds != null && categoryIds.length > 0) {
                    if (categoryIds.includes(categoryPropIds.displayValue)) {
                        res.push(this.allElements[i]);
                        elementDetected = true;
                    }
                }
                else if (!elementDetected && categoryPropNames != null && categories.length > 0) {
                    if (categories.includes(categoryPropNames.displayValue.replace(categoryPre, ''))) {
                        res.push(this.allElements[i]);
                        elementDetected = true;
                    }
                }
                else if (!elementDetected && categoryPropIds != null && categories.length > 0) {
                    if (res.includes(this.categoryIdNameDictionary[categoryPropIds.displayValue])) {
                        res.push(this.allElements[i]);
                        elementDetected = true;
                    }
                }
            }
        }
        return res;
    }

    async changeFilterBy(filter) {
        this.selectedFilter = filter;
        var filterValues = [];
        var allNumbers = true;

        for (var i = 0; i < this.categoryElements.length; i++) {
            var props = this.propertiesManager.GetProperties(this.categoryElements[i]);
            var prop = this.findByDisplayName(props, filter);

            if (prop) {
                var value = prop.displayValue;
                if (!filterValues.includes(value)) {
                    if (typeof value != "number")
                        allNumbers = false;
                    filterValues.push(value);
                }
            }
        }

        if (allNumbers)
            filterValues.sort(function (a, b) {
                return a - b;
            });
        else filterValues.sort();

        filterValues.unshift("All");

        var res = new DataForMenus();
        res.filterValues = filterValues;
        return res;
    }

    // Helper function to get pro parameter type based on parameter name
    getProParameterType(propertyName) {
        if (propertyName === PRO_PARAM_NAMES.itemNum || propertyName === PRO_PARAM_NAMES.desc || propertyName === PRO_PARAM_NAMES.calculationGroup) {
            return valueTypes.proText;
        } else if (propertyName === PRO_PARAM_NAMES.itemUnit) {
            return valueTypes.proEnumSingle;
        } else if (propertyName === PRO_PARAM_NAMES.constructionDate) {
            return valueTypes.proDate;
        }
        return null;
    }

    async getProperyInfo(propertyName) {
        var filterValues = [];
        var allNumbers = true;
        var allDates = true;

        for (var i = 0; i < this.categoryElements.length; i++) {
            var props = this.propertiesManager.GetProperties(this.categoryElements[i]);
            var prop = this.findByDisplayName(props, propertyName);

            if (prop) {
                var value = prop.displayValue;
                if (value == null)
                    value = "";
                if (typeof value == "number")
                    value = roundNumber(value);

                if (!filterValues.includes(value)) {
                    if (value != "") {

                        if (typeof value != "number")
                            allNumbers = false;
                        if (!(value.length == 10 && dateRegex.test(value)))
                            allDates = false;
                    }

                    filterValues.push(value);
                }
            }
        }

        if (allNumbers)
            filterValues.sort(function (a, b) {
                return a - b;
            });
        else filterValues.sort();

        var res = new PropertyInfo();

        // Check if it's a custom parameter
        if (Object.keys(this.customParametersObject.parameters).includes(propertyName)) {
            res.type = this.customParametersObject.parameters[propertyName].type;
        }
        // Check if it's a pro parameter
        else {
            const proParamType = this.getProParameterType(propertyName);
            if (proParamType !== null) {
                res.type = proParamType;
                // For proEnumSingle (itemUnit), add predefined values
                if (proParamType === valueTypes.proEnumSingle && propertyName === PRO_PARAM_NAMES.itemUnit) {
                    res.values = Object.values(QBS_Item_Unit_Values);
                }
            } else {
                // Default type detection for regular properties
                if (allNumbers)
                    res.type = valueTypes.number;
                else if (allDates)
                    res.type = valueTypes.date;
                else
                    res.type = valueTypes.text;
            }
        }

        // Only set filterValues if not already set (for proEnumSingle with predefined values)
        if (!res.values || res.values.length === 0) {
            res.values = filterValues;
        } else {
            // Merge predefined values with actual filter values
            const mergedValues = [...new Set([...res.values, ...filterValues])];
            res.values = mergedValues;
        }

        return res;
    }

    async getDateRange(propertyName) {
        var min = null, max = null;

        for (var i = 0; i < this.allElements.length; i++) {
            var props = this.propertiesManager.GetProperties(this.allElements[i]);
            var prop = this.findByDisplayName(props, propertyName);

            if (prop) {
                var value = prop.displayValue;
                if (value != null || value != "") {
                    var d = new Date(value);
                    if (d instanceof Date && !isNaN(d.getTime())) {
                        if (min == null || d < min)
                            min = new Date(d);
                        if (max == null || d > max)
                            max = new Date(d);
                    }
                }
            }
        }

        if (max instanceof Date && !isNaN(max.getTime()))
            max.setDate(max.getDate() + 1);

        if (min instanceof Date && !isNaN(min.getTime()))
            min.setDate(min.getDate() - 1);

        var res = new DateRange(min, max);

        return res;
    }

    async selectSecondFilterValues(values) {
        if (values.includes("All")) {
            this.filteredElements = this.categoryElements;
        } else {
            this.filteredElements = [];

            for (var i = 0; i < this.categoryElements.length; i++) {
                var props = this.propertiesManager.GetProperties(this.categoryElements[i]);
                var elementValue = this.findByDisplayName(props, this.selectedFilter);

                var passFilter =
                    elementValue && values.includes(elementValue.displayValue.toString());

                if (passFilter) this.filteredElements.push(this.categoryElements[i]);
            }
        }

        return this.getMenusDataAfterSecondFilter();
    }

    async selectSecondFilterValueWithOperator(operator, value) {
        this.filteredElements = [];

        for (var i = 0; i < this.categoryElements.length; i++) {
            var props = this.propertiesManager.GetProperties(this.categoryElements[i]);
            var elementprop = this.findByDisplayName(props, this.selectedFilter);

            if (elementprop && elementprop.displayValue != null) {
                var elementValue = elementprop.displayValue;
                var passFilter = false;

                switch (operator) {
                    case operatorValues.equal:
                        passFilter = elementValue == value;
                        break;
                    case operatorValues.notEqual:
                        passFilter = elementValue != value;
                        break;
                    case operatorValues.greater:
                        passFilter = elementValue > value;
                        break;
                    case operatorValues.less:
                        passFilter = elementValue < value;
                        break;
                    case operatorValues.greaterOrEqual:
                        passFilter = elementValue >= value;
                        break;
                    case operatorValues.lessOrEqual:
                        passFilter = elementValue <= value;
                        break;
                    default:
                        passFilter = false;
                }

                if (passFilter) this.filteredElements.push(this.categoryElements[i]);
            }
        }

        return this.getMenusDataAfterSecondFilter();
    }

    async selectTemplate(templateName, templateFolder = "Default") {

        var selectedTemplate;

        for (var i = 0; i < this.userTemplates[templateFolder].length; i++) {
            if (this.userTemplates[templateFolder][i].title == templateName) {
                selectedTemplate = this.userTemplates[templateFolder][i];
                break;
            }
        }

        var res = await this.selectCategory(selectedTemplate.categories, selectedTemplate.categoryIds);

        //var temp = await this.changeFilterBy(selectedTemplate.secondFilter);
        //res.filterValues = temp.filterValues;

        //temp = await this.selectSecondFilterValues(selectedTemplate.filterValues);
        //res.properties = temp.properties;
        //res.groupingOptions = temp.groupingOptions;
        //res.quickCounterData = temp.quickCounterData;

        var sharedPropertiesByDisplayName = this.getSharedPropertiesByDisplayName(this.filteredElements);
        var sharedPropertiesByAttributeName = this.getSharedPropertiesByAttributeName(this.filteredElements);

        var existingProps = [];
        var existingGroups = [];

        for (var i = 0; i < selectedTemplate.properties.length; i++) {
            var propDisplayName = null;

            if (sharedPropertiesByAttributeName.includes(selectedTemplate.properties[i])) {
                propDisplayName = this.sharedPropertiesDictionary[selectedTemplate.properties[i]];
            } else if (sharedPropertiesByDisplayName.includes(selectedTemplate.properties[i])) {
                propDisplayName = selectedTemplate.properties[i];
            }

            if (propDisplayName != null && !existingProps.includes(propDisplayName)) {
                existingProps.push(propDisplayName);
            }
        }

        for (var i = 0; i < selectedTemplate.groupBy.length; i++) {
            if (sharedPropertiesByAttributeName.includes(selectedTemplate.groupBy[i])) {
                existingGroups.push(this.sharedPropertiesDictionary[selectedTemplate.groupBy[i]]);
            }
            else if (sharedPropertiesByDisplayName.includes(selectedTemplate.groupBy[i])) {
                existingGroups.push(selectedTemplate.groupBy[i]);
            }
        }

        var localizedCategoryNames = selectedTemplate.categoryIds.map(id => this.categoryIdNameDictionary[id]);

        res.selection = new MenusSelection(
            localizedCategoryNames,
            selectedTemplate.secondFilter,
            selectedTemplate.filterValues,
            existingProps,
            existingGroups,
            selectedTemplate.searchValue
        );

        res.colorScheme = selectedTemplate.colorScheme;
        res.customColumns = selectedTemplate.customColumns;
        if (selectedTemplate.settings != null) {
            res.settings = JSON.parse(selectedTemplate.settings);
        }

        if (this.isTranslateModelEnabled) {
            const expressions = returnUniqueStrings([res.properties, res.settings.properties, res.settings.groups, res.settings.properties])
            const translations = await this.handleHebrewTranslation(expressions)

            res.translations = translations
        }
        
        return res;
    }

    async createSchedule(properties, groupingParameters) {
        if (properties.includes("All"))
            properties = this.getSharedPropertiesByDisplayName(this.filteredElements);

        if (groupingParameters.length == 0) {
            return await this.getInstancesSchedule(properties);
        }

        // find the groups
        var groups = [];
        var groupNames = [];
        groups.push(this.filteredElements);
        groupNames.push("");

        for (var i = 0; i < groupingParameters.length; i++) {
            var newgroups = [];
            var newGroupNames = [];

            for (var j = 0; j < groups.length; j++) {
                for (var k = 0; k < groups[j].length; k++) {
                    var props = this.propertiesManager.GetProperties(groups[j][k]);
                    var prop = this.findByDisplayName(props, groupingParameters[i], true);
                    var value = prop ? prop.displayValue : "Missing Parameter";
                    var name = groupNames[j] + "[" + value + "]";
                    if (newGroupNames.includes(name)) {
                        newgroups[newGroupNames.indexOf(name)].push(groups[j][k]);
                    } else {
                        var g = [];
                        g.push(groups[j][k]);
                        newgroups.push(g);
                        newGroupNames.push(name);
                    }
                }
            }

            groups = newgroups;
            groupNames = newGroupNames;
        }

        // sort groups by group names
        var nonSortedNames = [...groupNames];
        groupNames.sort();
        var sortedGroups = [];
        for (var i = 0; i < groupNames.length; i++) {
            sortedGroups.push(groups[nonSortedNames.indexOf(groupNames[i])]);
        }
        groups = sortedGroups;

        var sums = [];
        var units = [];
        var types = [];

        for (var i = 0; i < properties.length; i++) {
            sums.push(null);
            units.push(null);
            types.push(valueTypes.unknown);
        }

        var groupLines = [];

        for (var i = 0; i < groups.length; i++) {
            var line = new RowInfo(groups[i]);
            line[GroupingHeaderKey] = groupNames[i];
            line[CountHeaderKey] = groups[i].length;

            for (var j = 0; j < properties.length; j++) {
                var sum = 0;
                var allSame = true;
                var isNumber = false;
                var sameValue = null;

                for (var k = 0; k < groups[i].length; k++) {
                    var props = this.propertiesManager.GetProperties(groups[i][k]);
                    var prop = this.findByDisplayName(props, properties[j]);
                    var value = prop ? prop.displayValue : "";

                    if (typeof value == "number") {
                        var eUnits = true;
                        if (prop.units != null) eUnits = prop.units;
                        if (units[j] == null) units[j] = eUnits;
                        else if (units[j] != eUnits && value != 0) units[j] = "Mixed units";
                    }
                    if (typeof value == "number" && this.isSummable(properties[j])) {
                        allSame = false;
                        isNumber = true;
                        sum += value;

                    } else if (allSame) {
                        if (sameValue == null) sameValue = value;
                        if (sameValue instanceof Date && value instanceof Date) {
                            allSame = isSameDay(sameValue, value);
                        }
                        else {
                            allSame = sameValue == value;
                        }
                    }
                }

                var cellRes;

                if (allSame) cellRes = sameValue;
                else if (isNumber) {
                    cellRes = sum;
                    sums[j] = sums[j] == null ? sum : sums[j] + sum;
                } else cellRes = "";

                if (cellRes != "") {
                    if (typeof cellRes == "number") {
                        if (types[j] == valueTypes.unknown)
                            types[j] = valueTypes.number;
                    }
                    else {
                        if (cellRes.length == 10 && dateRegex.test(cellRes)) {
                            if (types[j] == valueTypes.unknown)
                                types[j] = valueTypes.date;
                            else if (types[j] == valueTypes.number)
                                types[j] = valueTypes.text;
                        }
                        else
                            types[j] = valueTypes.text;
                    }
                }

                line[properties[j]] = cellRes;
            }
            groupLines.push(line);
        }

        var groupHeader = "";
        for (var i = 0; i < groupingParameters.length; i++)
            groupHeader += "[" + groupingParameters[i] + "]";

        var res = new DataForTable();
        res.columnsInfo.push(new ColumnInfo(GroupingHeaderKey, groupHeader, valueTypes.text, null));
        res.columnsInfo.push(new ColumnInfo(CountHeaderKey, i18next.t('count'), valueTypes.number, null, true));


        for (var i = 0; i < properties.length; i++) {
            var unit = null;
            var propExtention = "";

            if (sums[i] != null) propExtention += " Σ";

            if (units[i] != null && units[i] != true) {
                var funit = formatUnit(units[i]);
                if (funit == "Mixed units")
                    propExtention += " [Mixed units]";
                else
                    unit = funit;
            }

            if (Object.keys(this.customParametersObject.parameters).includes(properties[i])) {
                types[i] = this.customParametersObject.parameters[properties[i]].type;
            } else {
                // Check if it's a pro parameter
                const proParamType = this.getProParameterType(properties[i]);
                if (proParamType !== null) {
                    types[i] = proParamType;
                }
            }
            res.columnsInfo.push(new ColumnInfo(properties[i], properties[i] + propExtention, types[i], unit, sums[i] != null));
        }

        res.rowsData = groupLines;

        return res;
    }

    async getTemplateInfo(templateName, TemplateFolder) {
        //Find template
        var selectedTemplate;

        if (this.userTemplates[TemplateFolder] != null)
            selectedTemplate = this.userTemplates[TemplateFolder].find(element => element.title == templateName)

        if (selectedTemplate == undefined) {
            logError("FrontEnd.utils.builder.Bimsearch.selectedTemplate", "can't find template - " + templateName);
            throw (i18next.t('cant_find_template'));
        }

        var categoryElements = await this.findElementsByCategories(selectedTemplate.categories, selectedTemplate.categoryIds);

        return new QuickInfo(selectedTemplate.creationTime, this.getCounterData(categoryElements), selectedTemplate.properties, selectedTemplate.description);
    }

    // iterates all elements in current model and returns Object { string: 
    //    {
    //       monthCollections: Collection[]
    //       totalAssigned: Number
    //    }
    // }
    async CreateCollectionsForExecutionReport() {
        // gets a JS Date instance, returns yyyy/mm
        function getDate(date) {
            try {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                return `${year}/${month}`;
            }
            catch (e) {
                return ""
            }
        }

        const rawItemNumsData = {}
        
        const itemNumValues = this.customParametersObject.values[PRO_PARAM_NAMES.itemNum] || {}
        const itemNums = Object.keys(itemNumValues)
        if (!itemNums.length){
            return
        }

        for (let i = 0; i < this.allElements.length; i++) {
            const elementId = this.allElements[i]
            const elementProps = this.propertiesManager.GetProperties(elementId);

            const propItemNum = this.findByDisplayName(elementProps, PRO_PARAM_NAMES.itemNum);
            const valueItemNum = propItemNum?.displayValue
            if (!valueItemNum)
                continue
            
            if (!rawItemNumsData[valueItemNum]) {
                const newBucket = {
                    totalAssigned: 0,
                    elementCount: 0,  // Track element count for Unit fallback
                    elementsByMonth: {},
                    itemUnit: null,
                    itemUnitMeasurement: null,
                    description: "",
                    groupBy: PRO_PARAM_NAMES.calculationGroup,
                    // Track unique values for resolution
                    uniqueUnits: new Set(),
                    uniqueMeasurements: new Set(),
                }
                rawItemNumsData[valueItemNum] = newBucket
            }

            const itemNumBucket = rawItemNumsData[valueItemNum]

            const propItemUnit = this.findByDisplayName(elementProps, PRO_PARAM_NAMES.itemUnit);
            const valForTotalAssigned = this.getValueToAddUpTotalAssigned(elementProps, propItemUnit?.displayValue)

            itemNumBucket.totalAssigned += valForTotalAssigned
            
            // Collect itemUnit and measureUnit for this element
            const valueItemUnit = QBS_Item_Unit_Values[propItemUnit?.displayValue] || null;
            let measUnit = null;
            
            if (valueItemUnit) {
                const valueItemUnitDescription = this.findByDisplayName(elementProps, valueItemUnit);
                measUnit = valueItemUnitDescription?.units ? formatUnit(valueItemUnitDescription.units) : null;
            }
            
            // Track unique units and measurements
            itemNumBucket.uniqueUnits.add(valueItemUnit);  // Can add null
            itemNumBucket.uniqueMeasurements.add(measUnit);  // Can add null
            
            // Update description
            const propDescription = this.findByDisplayName(elementProps, PRO_PARAM_NAMES.desc);
            const valueDescription = propDescription?.displayValue;
            itemNumBucket.description = valueDescription ? valueDescription : itemNumBucket.description;
            
            // if element has itemNum and date -> add to selection set
            // if element has itemNum and no date -> only totalAssigned/itemUnit/description updated above
            
            const propConstructionDate = this.findByDisplayName(elementProps, PRO_PARAM_NAMES.constructionDate);
            let valueConstructionDate = propConstructionDate?.displayValue
            if (!valueConstructionDate)
                continue
            valueConstructionDate = new Date(valueConstructionDate)
            
            const selectionSetDate = getDate(valueConstructionDate)
            if (!selectionSetDate)
                continue
            
            itemNumBucket.elementCount += 1  // Count elements for Unit fallback

            if (itemNumBucket.elementsByMonth[selectionSetDate]) {
                itemNumBucket.elementsByMonth[selectionSetDate].push(elementId)
            } else {
                itemNumBucket.elementsByMonth[selectionSetDate] = [elementId]
            }
        }

        // Resolve itemUnit and itemUnitMeasurement for each bucket
        for (let itemNum of Object.keys(rawItemNumsData)) {
            const bucket = rawItemNumsData[itemNum];
            const uniqueUnits = Array.from(bucket.uniqueUnits);
            const uniqueMeasurements = Array.from(bucket.uniqueMeasurements);

            // Filter out null values to check what actual units exist
            const nonNullUnits = uniqueUnits.filter(u => u !== null);
            const nonNullMeasurements = uniqueMeasurements.filter(m => m !== null);

            if (nonNullUnits.length === 0) {
                // Case 4: ALL elements have no unit assigned
                bucket.itemUnit = "Unit";
                bucket.itemUnitMeasurement = null;
                bucket.totalAssigned = bucket.elementCount;  // Use element count for Unit fallback
            } else if (nonNullUnits.length === 1 && uniqueUnits.length === 1) {
                // All elements have the same unit (no nulls mixed in)
                bucket.itemUnit = nonNullUnits[0];

                if (nonNullMeasurements.length === 0) {
                    // No measurements (e.g., "Unit" type)
                    bucket.itemUnitMeasurement = null;
                } else if (nonNullMeasurements.length === 1 && uniqueMeasurements.length === 1) {
                    // Case 1: ALL elements have same unit AND same measurement
                    bucket.itemUnitMeasurement = nonNullMeasurements[0];
                } else {
                    // Case 2: Same unit but different measurements (or some null, some not)
                    bucket.itemUnitMeasurement = "mixed";
                }
            } else {
                // Case 3: Elements have different units (or mix of null and non-null units)
                bucket.itemUnit = "Unit";
                bucket.itemUnitMeasurement = null;
                bucket.totalAssigned = bucket.elementCount;  // Use element count for Unit fallback
            }

            // Clean up temporary tracking
            delete bucket.uniqueUnits;
            delete bucket.uniqueMeasurements;
            delete bucket.elementCount;
        }

        // after sorting elements into itemNum buckets we can create a collection for each month
        // end goal - each itemNum will be a dir which will contains collections by month

        const result = {}

        for (let itemNum of Object.keys(rawItemNumsData)) {
            const rawBucket = rawItemNumsData[itemNum]

            const newItemNumResult = {
                monthCollections: [],
                totalAssigned: rawBucket.totalAssigned,
                itemUnit: rawBucket.itemUnit,
                itemUnitMeasurement: rawBucket.itemUnitMeasurement,
                description: rawBucket.description
            }
            console.log(itemNum);
            console.log(newItemNumResult);
            
            for (let dateString of Object.keys(rawBucket.elementsByMonth)) {

                const elements = [...rawBucket.elementsByMonth[dateString]]
                
                const itemUnit = rawBucket.itemUnit
                console.log(`itemUnit: ${itemUnit}`);
                
                let properties = []
                if (!itemUnit || itemUnit === QBS_Item_Unit_Values.Unit) {
                    properties = []
                } else {
                    properties = [itemUnit]
                }
                console.log(properties);
                
                const monthCollection = await createCollection(elements, properties, [rawBucket.groupBy])

                monthCollection.title = dateString
                monthCollection.descreption = rawBucket.description
                newItemNumResult.monthCollections.push(monthCollection)
            }
            result[itemNum] = newItemNumResult
        }

        return result
    }
    async fetchExecutionReport() {
        const executionReports = await getFromServer(`/api/forge/userdata/executionCollections?modelUrl=${this.modelUrl}`)
        this.executionReports = executionReports
    }
    async generateExecutionReport() {
        
        const itemNumsData = await this.CreateCollectionsForExecutionReport()

        if (!Object.keys(itemNumsData || {}).length) {
            console.log("You have no valid data for execution report!\n element must include for the very least QBS_Item_number and <$>Construction date")
            return
        }
        
        const budgetItems = this.mapCollectionsToBudgetsItemsData(itemNumsData)
        const reportSelections = {}
        Object.entries(itemNumsData).forEach(([key, value]) => {
            reportSelections[key] = value.monthCollections
        })

        const executionReport = {}

        executionReport.id = this.modelUrl
        executionReport.modelUrl = this.modelUrl
        executionReport.reportSelections = reportSelections
        executionReport.budgetItemsData = budgetItems
        executionReport.projectName = "" // project name and date will be resolved in server

        await postToServer("/api/forge/userdata/executionCollections", executionReport)
    }
    async getReportFolders() {
        try {
            return Object.keys(this.executionReports?.reportSelections) || [];
        } catch {
            return []
        }
    }
    async getFullExecutionReport() {
        try {
            await this.fetchExecutionReport()
            return this.executionReports
        } catch {
            return new ExecutionReport()
        }
    }
    getReportCollectionsForFolder(folderName) {
        try {
            return this.executionReports.reportSelections[folderName]
        } catch {
            return []
        }
    }

    // receives
    //      itemNumsData from this.CreateCollectionsForExecutionReport result
    // returns
    //      Dictionary<string, budgetItemData> item number => budgetItemData
    mapCollectionsToBudgetsItemsData(itemNumsData) {
        const result = {}
        for (let itemNumber of Object.keys(itemNumsData || {})) {
            const budgetItem = new BudgetItemData()
            budgetItem.itemNumber = itemNumber
            budgetItem.description = itemNumsData[itemNumber].description || ""
            budgetItem.unit = itemNumsData[itemNumber].itemUnit || ""
            budgetItem.unitMeasurement = itemNumsData[itemNumber].itemUnitMeasurement || null
            budgetItem.totalAssigned = itemNumsData[itemNumber].totalAssigned
            result[itemNumber] = budgetItem
        }

        return result
    }
    // elementProps - an object of element properties
    // itemUnit     - QBS_Item_Unit_Values member
    getValueToAddUpTotalAssigned(elementProps, itemUnit) {
        if (itemUnit === QBS_Item_Unit_Values.Unit)
            return 1

        try {
            
            const propDescription = this.findByDisplayName(elementProps, itemUnit);
            return Number(propDescription.displayValue)
        } catch {
            // can get here only if user assign itemUnit that does not belong to an element 
            return 0
        }
    }

    async getCollectionInfo(collection) {
        var quickData = [];
        quickData.push(new QuickDataField(i18next.t('count'), collection.elements.length));
        getCollectionDefaultProperties().forEach(({ label, accessor }) => {
            var sum = 0;
            var unit = null;
            collection.elements.forEach(element => {
                var propValue = element.propertiesValues[accessor];
                if (propValue != null) {
                    sum += propValue.displayValue;
                    if (unit == null)
                        unit = propValue.units;
                    else if (propValue.units != unit && propValue.displayValue != 0)
                        unit = "Mixed";
                }
            });
            quickData.push(new QuickDataField(label, sum, formatUnit(unit)));
        });
        return new QuickInfo(collection.creationTime, quickData, collection.properties, collection.descreption);
    }

    getDetailedCollectionInfo(collection) {
        var res = new Object();
        collection.elements.forEach(element => {
            var category = "Undefined";
            var elementCategory = element.propertiesValues[CategoryParameterName];
            if (elementCategory != null && elementCategory.displayValue != null)
                category = elementCategory.displayValue;

            if (res[category] == null) {
                res[category] = [];
                res[category].push(new QuickDataField(i18next.t('count')))
                getCollectionDefaultProperties().forEach(({ label }) => {
                    res[category].push(new QuickDataField(label));
                });
            }

            ++res[category][0].value;

            for (var i = 0; i < collectionDefaultProperties.length; i++) {
                var elementProp = element.propertiesValues[collectionDefaultProperties[i]];
                if (elementProp != null && elementProp.displayValue != null && typeof elementProp.displayValue == "number") {
                    res[category][i+1].value += elementProp.displayValue;
                    if (elementProp.units != null) {
                        var unit = formatUnit(elementProp.units);
                        if (res[category][i+1].unit == null)
                            res[category][i + 1].unit = unit;
                        else if (res[category][i + 1].unit != unit)
                            res[category][i+1].unit = "Mixed";
                    }
                }
            }
        });

        return res;
    }

    addTemplateFolder(folderName) {
        if (Object.keys(this.userTemplates).includes(folderName))
            throw (i18next.t('folder_already_exist'));
        else {
            this.userTemplates[folderName] = [];
            this.updateTemplatesFolderOnDatabase();
        }

        return this.getTemplateNames();
    }

    deleteTemplateFolder(folderName) {
        if (folderName == DefaultTemplatesnFolder)
            throw i18next.t('cant_delete_default_templates_folder');

        if (!Object.keys(this.userTemplates).includes(folderName))
            throw (i18next.t('folder_doesnt_exist'));
        else {
            delete this.userTemplates[folderName];
            this.updateTemplatesFolderOnDatabase();
        }

        return this.getTemplateNames();
    }

    renameTemplateFolder(originalName, newName) {
        if (originalName == DefaultTemplatesnFolder)
            throw i18next.t('cant_rename_default_templates_folder');

        if (Object.keys(this.userTemplates).includes(newName))
            throw (i18next.t('folder_already_exist'));
        if (!Object.keys(this.userTemplates).includes(originalName))
            throw (i18next.t('folder_doesnt_exist'));

        this.userTemplates[newName] = this.userTemplates[originalName];
        delete this.userTemplates[originalName];
        this.updateTemplatesFolderOnDatabase();

        return this.getTemplateNames();
    }

    getCounterData(elements) {
        var res = [];
        res.push(new QuickDataField(i18next.t('count')));
        res.push(new QuickDataField(i18next.t('length')));
        res.push(new QuickDataField(i18next.t('area')));
        res.push(new QuickDataField(i18next.t('volume')));

        for (var i = 0; i < elements?.length; i++) {
            var props = this.propertiesManager.GetProperties(elements[i]);
            this.addElementToDataArray(res, props);
        }

        return res;
    }

    getDataByCategory(elements) {
        var res = new Object();

        for (var i = 0; i < elements?.length; i++) {
            var props = this.propertiesManager.GetProperties(elements[i]);
            var categoryProp = this.findByDisplayName(props, CategoryParameterName);

            var categoryName = "Unknown";
            if (categoryProp != null)
                categoryName = categoryProp.displayValue;

            if (res[categoryName] == null) {
                res[categoryName] = [];
                res[categoryName].push(new QuickDataField(i18next.t('count')));
                res[categoryName].push(new QuickDataField(i18next.t('length')));
                res[categoryName].push(new QuickDataField(i18next.t('area')));
                res[categoryName].push(new QuickDataField(i18next.t('volume')));
            }

            this.addElementToDataArray(res[categoryName], props);
        }

        return res;
    }

    saveTemplate(templateFolder, title, description, properties, groupBy, searchValue, customColumns, colorScheme, settings) {
        if (this.userTemplates[templateFolder] == null)
            this.userTemplates[templateFolder] = [];

        this.deleteTemplateNoSync(templateFolder, title);

        var categoriesIds = [];

        Object.keys(this.categoryIdNameDictionary).forEach(categoryId => {
            if (this.selectedCategories.includes(this.categoryIdNameDictionary[categoryId]))
                categoriesIds.push(Number(categoryId));
        });

        var template = new Template(title, description, this.selectedCategories, categoriesIds, properties, groupBy, searchValue, customColumns, colorScheme, settings);

        this.userTemplates[templateFolder].push(template);
        this.updateTemplatesFolderOnDatabase();

        return this.getTemplateNames();
    }

    deleteTemplate(templateFolder, title) {
        if (!this.deleteTemplateNoSync(templateFolder, title))
            throw i18next.t('cant_find_template');
        this.updateTemplatesFolderOnDatabase();

        return this.getTemplateNames();
    }

    editTemplate(ogrinalTemplateFolder, originalTitle, newTemplateFolder, newTitle, newDescription) {

        var template;
        if (this.userTemplates[ogrinalTemplateFolder] != null)
            template = this.userTemplates[ogrinalTemplateFolder].find(element => element.title == originalTitle);


        if (template == undefined)
            throw i18next.t('cant_find_template');

        if (!(ogrinalTemplateFolder == newTemplateFolder && originalTitle == newTitle) && this.userTemplates[newTemplateFolder].findIndex(element => element.title == newTitle) != -1) {
            throw i18next.t('new_name_already_taken');
        }

        this.deleteTemplateNoSync(ogrinalTemplateFolder, originalTitle);

        template.description = newDescription;
        template.title = newTitle;

        if (this.userTemplates[newTemplateFolder] == null)
            this.userTemplates[newTemplateFolder] = [];

        this.userTemplates[newTemplateFolder].push(template);

        this.updateTemplatesFolderOnDatabase();
        return this.getTemplateNames();
    }

    async addElementsToCollection(collection, elements) {
        var elementProperties = [...collection.properties];
        collection.groupBy.forEach(prop => {
            if (!elementProperties.includes(prop))
                elementProperties.push(prop);
        });
        getCollectionDefaultProperties().forEach(({ label }) => {
            if (!elementProperties.includes(label))
                elementProperties.push(label);
        });
        if (!elementProperties.includes(CategoryParameterName))
            elementProperties.push(CategoryParameterName);

        await this.removeElementsFromCollection(collection, elements);

        elements.forEach(element => {
            var collectionElement = new BimsearchElement(this.propertiesManager.viewerIdToModelId[element]);
            var props = this.propertiesManager.properties[element];
            elementProperties.forEach(prop => {
                collectionElement.propertiesValues[prop] = this.findByDisplayName(props, prop);
            });

            collection.elements.push(collectionElement);
        });
    }


    async removeElementsFromCollection(collection, elements) {
        var newElements = collection.elements.filter(element => !elements.includes(this.propertiesManager.modelIdToViewerId[modelIdToKey(element.id)]) );
        collection.elements = newElements;
    }


    async getCollectionTable(collection) {

        var res = new DataForTable();

        var sums = [];
        var units = [];
        var types = [];

        for (var i = 0; i < collection.properties.length; i++) {
            sums.push(null);
            units.push(null);
            types.push(valueTypes.unknown);
        }

        for (var i = 0; i < collection.elements.length; i++) {
            for (var j = 0; j < collection.properties.length; j++) {
                var prop = collection.elements[i].propertiesValues[collection.properties[j]];

                if (prop) {
                    if (typeof prop.displayValue == "number") {
                        var eUnits = true;
                        if (prop.units != null) eUnits = prop.units;
                        if (units[j] == null) units[j] = eUnits;
                        else if (units[j] != eUnits && prop.displayValue != 0)
                            units[j] = "Mixed units";
                    }
                    if (typeof prop.displayValue == "number" && this.isSummable(collection.properties[j])) {
                        sums[j] = sums[j] == null ? prop.displayValue : sums[j] + prop.displayValue;
                    }
                }
            }
        }
        console.log(collection);
        
        if (collection.groupBy != null && collection.groupBy.length > 0) {
            var groupHeader = "";
            for (var i = 0; i < collection.groupBy.length; i++)
                groupHeader += "[" + collection.groupBy[i] + "]";

            res.columnsInfo.push(new ColumnInfo(GroupingHeaderKey, groupHeader, valueTypes.text, null));
            res.columnsInfo.push(new ColumnInfo(CountHeaderKey, i18next.t('count'), valueTypes.number, null, true));

            // find the groups
            var groups = [];
            var groupNames = [];
            groups.push(collection.elements);
            groupNames.push("");

            for (var i = 0; i < collection.groupBy.length; i++) {
                var newgroups = [];
                var newGroupNames = [];

                for (var j = 0; j < groups.length; j++) {
                    for (var k = 0; k < groups[j].length; k++) {
                        var props = this.propertiesManager.GetProperties(groups[j][k]);
                        var prop = groups[j][k].propertiesValues[collection.groupBy[i]];
                        var value = prop ? prop.displayValue : "Missing Parameter";
                        var name = groupNames[j] + "[" + value + "]";
                        if (newGroupNames.includes(name)) {
                            newgroups[newGroupNames.indexOf(name)].push(groups[j][k]);
                        } else {
                            var g = [];
                            g.push(groups[j][k]);
                            newgroups.push(g);
                            newGroupNames.push(name);
                        }
                    }
                }
                groups = newgroups;
                groupNames = newGroupNames;
            }

            // sort groups by group names
            var nonSortedNames = [...groupNames];
            groupNames.sort();
            var sortedGroups = [];
            for (var i = 0; i < groupNames.length; i++) {
                sortedGroups.push(groups[nonSortedNames.indexOf(groupNames[i])]);
            }
            groups = sortedGroups;


            var groupLines = [];
            for (var i = 0; i < groups.length; i++) {
                var line = new RowInfo([]);
                line[GroupingHeaderKey] = groupNames[i];
                line[CountHeaderKey] = groups[i].length;

                for (var j = 0; j < collection.properties.length; j++) {
                    var sum = 0;
                    var allSame = true;
                    var isNumber = false;
                    var sameValue = null;

                    for (var k = 0; k < groups[i].length; k++) {
                        var props = this.propertiesManager.GetProperties(groups[i][k]);
                        var prop = groups[i][k].propertiesValues[collection.properties[j]];
                        var value = prop ? prop.displayValue : "";

                        if (typeof value == "number" && this.isSummable(collection.properties[j])) {
                            allSame = false;
                            isNumber = true;
                            sum += value;
                        } else if (allSame) {
                            if (sameValue == null)
                                sameValue = value;
                            allSame = sameValue == value;
                        }
                    }

                    var cellRes = "";

                    if (allSame)
                        cellRes = sameValue;
                    else if (isNumber) {
                        cellRes = sum;
                    }
                    line[collection.properties[j]] = cellRes;

                    if (cellRes != "") {
                        if (typeof cellRes == "number") {
                            if (types[j] == valueTypes.unknown)
                                types[j] = valueTypes.number;
                        }
                        else {
                            if (cellRes.length == 10 && dateRegex.test(cellRes)) {
                                if (types[j] == valueTypes.unknown)
                                    types[j] = valueTypes.date;
                                else if (types[j] == valueTypes.number)
                                    types[j] = valueTypes.text;
                            }
                            else
                                types[j] = valueTypes.text;
                        }
                    }
                }

                var lineElements = [];
                groups[i].forEach(element => {
                    var viewerId = this.propertiesManager.modelIdToViewerId[modelIdToKey(element.id)];
                    if (viewerId != null)
                        lineElements.push(viewerId);
                });
                line.lineElements = lineElements;

                groupLines.push(line);
            }

            res.rowsData = groupLines;
        }
        else {
            for (var i = 0; i < collection.elements.length; i++) {
                var lineElements = [];
                var viewerId = this.propertiesManager.modelIdToViewerId[modelIdToKey(collection.elements[i].id)];
                if (viewerId != null)
                    lineElements.push(viewerId);

                var lineData = new RowInfo(lineElements);

                lineData[ElementHeaderKey] = collection.elements[i].id.revitId;

                for (var j = 0; j < collection.properties.length; j++) {
                    var prop = collection.elements[i].propertiesValues[collection.properties[j]];
                    var value = "";
                    if (prop != null && prop.displayValue != null)
                        value = prop.displayValue;

                    lineData[collection.properties[j]] = value;

                    if (value != "") {
                        if (typeof value == "number") {
                            if (types[j] == valueTypes.unknown)
                                types[j] = valueTypes.number;
                        }
                        else {
                            if (value.length == 10 && dateRegex.test(value)) {
                                if (types[j] == valueTypes.unknown)
                                    types[j] = valueTypes.date;
                                else if (types[j] == valueTypes.number)
                                    types[j] = valueTypes.text;
                            }
                            else
                                types[j] = valueTypes.text;
                        }
                    }
                }
                res.rowsData.push(lineData);
            }

            res.columnsInfo.push(new ColumnInfo(ElementHeaderKey, i18next.t('element'), valueTypes.text, null));
        }

        for (var i = 0; i < collection.properties.length; i++) {
            var headerProp = collection.properties[i];

            var unit = null;
            var headerExtention = "";
            if (sums[i] != null)
                headerExtention += " Σ";

            if (units[i] == "Mixed units")
                headerExtention += " (Mixed units)";
            if (units[i] != true && units[i] != "Mixed units")
                unit = formatUnit(units[i]);

            var totalVal = "";
            if (sums[i] != null) {
                totalVal = sums[i];
            }

            res.columnsInfo.push(new ColumnInfo(headerProp, headerProp + headerExtention, types[i], unit, sums[i] != null));
        }

        if (this.isTranslateModelEnabled) {
            const expressions = returnUniqueStrings([collection.properties, collection.groupBy, collection.categories])
            const translations = await this.handleHebrewTranslation(expressions)
            res.translations = translations
        }
        
        return res;
    }

    async applyFilters(filtersSchema) {
        this.filteredElements = await this.filterElements(this.categoryElements, filtersSchema);

        var res = new DataForMenus();
        res.quickCounterData = this.getCounterData(this.filteredElements);

        return res;
    }

    // get a list of elements and flters schema, and return a sub list that pass the filters
    async filterElements(elements, filtersSchema) {
        var res = elements;
        
        if (filtersSchema != null) {
            var filteringParameters = Object.keys(filtersSchema);

            if (filteringParameters.length > 0) {
                res = [];

                elements.forEach(elementId => {
                    var props = this.propertiesManager.GetProperties(elementId);

                    var passFilters = true;
                    for (var i = 0; i < filteringParameters.length && passFilters; i++) {
                        var filterScheme = filtersSchema[filteringParameters[i]];
                        var prop = this.findByDisplayName(props, filteringParameters[i]);
                        var value = "";
                        if (prop != null && prop.displayValue != null)
                            value = prop.displayValue;

                        var passConditions = false;
                        if (!filterScheme.conditions?.length)
                            passConditions = true;
                        else
                            for (var j = 0; j < filterScheme.conditions.length && !passConditions; j++) {

                                if (typeof value == "number")
                                    value = roundNumber(value);
                                passConditions = passConditions || parseConditionHelper({ type: filterScheme.conditions[j].type, currentValue: value, dataArray: filterScheme.conditions[j].dataArray, columnType: filterScheme.columnType });
                            }

                        passFilters = passFilters && passConditions;
                    }

                    if (passFilters)
                        res.push(elementId);
                });
            }
        }

        return res;
    }

    async createCustomParameter(name, type, values = null) {
        if (this.getAdditionalParameterNames().includes(name) || savedParameterNames.includes(name)) {
            throw i18next.t('name_not_allowed');
        }

        if (Object.keys(this.customParametersObject.parameters).includes(name)) {
            throw i18next.t('given_name_already_exists');
        }

        var newParam = new CustomParameter(name, type, values);
        this.customParametersObject.parameters[name] = newParam;

        this.customParametersObject.values[name] = {};

        var dataToSend = {};
        dataToSend.modelUrl = this.modelUrl;
        dataToSend.parameter = newParam;

        var url = "/api/forge/customparameters/addparameter";
        await postToServer(url, dataToSend);

    }

    async deleteCustomParameter(name) {
        if (!Object.keys(this.customParametersObject.parameters).includes(name)) {
            throw i18next.t('parameter_wasnt_found');
        }

        delete this.customParametersObject.parameters[name];
        delete this.customParametersObject.values[name];

        var dataToSend = {};
        dataToSend.name = name;
        dataToSend.modelUrl = this.modelUrl;
        var url = "/api/forge/customparameters/deleteparameter";

        await postToServer(url, dataToSend);
    }

    async getCustomParameters() {
        // duplicate custom parameters to prevent changing from outside
        var res = [];
        
        Object.values(this.customParametersObject.parameters).forEach(cp => {
            var newCp = CustomParameter.Copy(cp);
            res.push(newCp);
        });

        return res;
    }

    async getDateParameters() {
        // duplicate custom parameters to prevent changing from outside
        var res = [];

        Object.values(this.customParametersObject.parameters).forEach(cp => {
            if (cp.type == valueTypes.customDate || cp.type == 22) { 
                var newCp = CustomParameter.Copy(cp);
                res.push(newCp);
            }
        });

        return res;
    }

    async getCustomParameterValue(parameterName, element) {
        if (!Object.keys(this.customParametersObject.parameters).includes(parameterName)) {
            throw i18next.t('parameter_not_found');
        }
        
        var modelId = modelIdToKey(this.propertiesManager.viewerIdToModelId[element]);

        return this.customParametersObject.values[parameterName][modelId];
    }

    async setCustomParameterValues(parameterName, elements, value) {
        if (!Object.keys(this.customParametersObject.parameters).includes(parameterName)) {
            throw i18next.t('parameter_not_found');
        }

        var modelIds = [];
        elements.forEach(element => {
            modelIds.push(modelIdToKey(this.propertiesManager.viewerIdToModelId[element]));
        });

        var dataToSend = {};
        dataToSend.modelUrl = this.modelUrl;
        dataToSend.parameterName = parameterName;
        dataToSend.value = value;
        dataToSend.parametersIds = modelIds;

        var url = "/api/forge/customparameters/updateparametersvalue";
        await postToServer(url, dataToSend);

        modelIds.forEach(element => {
            this.customParametersObject.values[parameterName][element] = value;
        });
    }

    async getCommonParametersValues(elementId) {
        var propertiesValues = {};

        var elementProperties = this.getSharedPropertiesByDisplayName([elementId]);
        var commonElementProperties = elementProperties.filter(property => this.commonProperties.includes(property));

        var props = this.propertiesManager.GetProperties(elementId);

        commonElementProperties.forEach(propertyName => {
            var prop = this.findByDisplayName(props, propertyName);
            if (prop != null && prop.displayValue != null && prop.displayValue != "") {
                propertiesValues[propertyName] = prop.displayValue;
            }
        });

        return propertiesValues;
    }

   
    async editCustomParameter(oldName, newName, prevType, selectedType, values) {
        const isParamNameChanged = oldName !== newName
        if (isParamNameChanged && (this.getAdditionalParameterNames().includes(newName) || savedParameterNames.includes(newName))) {
            throw i18next.t('name_not_allowed');
        }
        if (isParamNameChanged && (Object.keys(this.customParametersObject.parameters).includes(newName))) {
            throw i18next.t('given_name_already_exists');
        }
        if (!Object.keys(this.customParametersObject.parameters).includes(oldName)) {
            throw i18next.t('parameter_wasnt_found');
        }
        // throw duplicate value error?
        const dataToSend = {
            modelUrl: this.modelUrl,
            oldParameterName: oldName,
            newParameterName: newName,
            prevType,
            selectedType,
            values
        }

        const editCpurl = "/api/forge/customparameters/editparameter";
        await postToServer(editCpurl, dataToSend);

        const getCpUrl = `/api/forge/customparameters/model?model=${this.modelUrl}`;
        this.customParametersObject = await getFromServer(getCpUrl);
    }

    // Private:


    // deletes the template without Syncing with cloud
    // return true if an element was deleted false if it was not found
    deleteTemplateNoSync(templateFolder, title) {
        var index = -1;
        if (this.userTemplates[templateFolder] != null)
            index = this.userTemplates[templateFolder].findIndex(element => element.title == title);

        if (index >= 0)
            this.userTemplates[templateFolder].splice(index, 1);

        return index >= 0;
    }

    getTemplateNames() {
        var res = new Object();

        Object.keys(this.userTemplates).forEach(key => {
            this.userTemplates[key].sort((a, b) => a.title < b.title ? -1 : 1);
            res[key] = [];
            this.userTemplates[key].forEach(temp => {
                res[key].push(temp.title);
            });
        });

        return res;
    }

    async updateTemplatesFolderOnDatabase() {
        try {
            postToServer("/api/forge/userdata/templates", this.userTemplates);
        }
        catch (err) {
            logError("FrontEnd.src.utils.builder.BimSearch.updateTemplatesFolderOnDatabase", err.toString());
        }
    }

    getSharedPropertiesByDisplayName(ids) {
        var propertyNames = [];
        var properties = this.getValidProperties(ids);
        var propertyNames = properties.map(property => property.displayName);

        if (ids.length > 0) {
            var additionalParameterNames = this.getAdditionalParameterNames();

            for (var i = 0; i < additionalParameterNames.length; i++) {
                propertyNames.push(additionalParameterNames[i]);
            }
        }

        propertyNames.sort();
        var uniquePropertyNames = [...new Set(propertyNames)];

        return uniquePropertyNames;
    }

    getSharedPropertiesByAttributeName(ids) {
        var propertyNames = [];
        var properties = this.getValidProperties(ids);
        var propertyNames = properties.map(property => property.attributeName);

        if (ids.length > 0) {
            var additionalParameterNames = this.getAdditionalParameterNames();

            for (var i = 0; i < additionalParameterNames.length; i++) {
                propertyNames.push(additionalParameterNames[i]);
            }
        }

        propertyNames.sort();
        var uniquePropertyNames = [...new Set(propertyNames)];

        return uniquePropertyNames;
    }

    getAdditionalParameterNames() {
        var nonRevitPropNames = [VolumeGeometry, SurfaceGeometry, VolumeGeometryCm, SurfaceGeometryCm];
        var revitPropNames = [CategoryParameterName, LinkDemiParameterName, FamilyDemiParameterName, BimsearchLevel, VolumeGeometry, SurfaceGeometry, VolumeGeometryCm, SurfaceGeometryCm];
        return this.isRvt() ? revitPropNames : nonRevitPropNames;
    }

    getValidProperties(ids) {
        var properties = [];
        var proprertyNames = [];

        for (var i = 0; i < ids.length; i++) {
            var props = this.propertiesManager.GetProperties(ids[i]);

            if (props && props.properties && props.properties.length != null)
                for (var j = 0; j < props.properties.length; j++)
                    if (
                        (!proprertyNames.includes(props.properties[j].displayName) || !proprertyNames.includes(props.properties[j].attributeName)) &&
                        !props.properties[j].hidden &&
                        props.properties[j].displayValue != null &&
                        props.properties[j].displayValue !== "" &&
                        ((this.isRvt() && !props.properties[j].hidden) ||
                            (!this.isRvt() && !props.properties[j].displayName.startsWith("Current Control point -") && !props.properties[j].displayName.startsWith("Vertex -")))
                    ) {
                        properties.push(props.properties[j]);
                        proprertyNames.push(props.properties[j].attributeName);
                        proprertyNames.push(props.properties[j].displayName);
                    }
        }

        return properties;
    }

    updatePropertiesTranslations() {
        var ids = this.allElements;
        var propsDictionary = [];

        for (var i = 0; i < ids.length; i++) {
            var props = this.propertiesManager.GetProperties(ids[i]);

            if (props && props.properties && props.properties.length != null)
                for (var j = 0; j < props.properties.length; j++)
                    if (!(props.properties[j].attributeName in propsDictionary) &&
                        !props.properties[j].hidden &&
                        props.properties[j].displayValue != null &&
                        props.properties[j].displayValue !== ""
                    ) {
                        propsDictionary[props.properties[j].attributeName] = props.properties[j].displayName;
                    }
        }

        if (ids.length > 0) {
            var additionalParameterNames = this.getAdditionalParameterNames();
            for (var i = 0; i < additionalParameterNames.length; i++) {
                propsDictionary[additionalParameterNames[i]] = additionalParameterNames[i];
            }
        }

        propsDictionary.sort();

        this.sharedPropertiesDictionary = propsDictionary;

        for (var i = 0; i < CommonProperties.length; i++) {
            var value = this.sharedPropertiesDictionary[CommonProperties[i]];
            if (value != null && !this.commonProperties.includes(value))
                this.commonProperties.push(value);
        }

        for (var i = 0; i < CommonGroupingOptions.length; i++) {
            var value = this.sharedPropertiesDictionary[CommonGroupingOptions[i]];
            if (value != null && !this.commonGroupingOptions.includes(value))
                this.commonGroupingOptions.push(value);
        }

        this.nonSummableProperties = [...NonSummableProperties];
        for (var i = 0; i < NonSummableProperties.length; i++) {
            if (this.sharedPropertiesDictionary[NonSummableProperties[i]] != null) {
                this.nonSummableProperties.push(this.sharedPropertiesDictionary[NonSummableProperties[i]]);
            }
        }
    }

    isSummable(property) {
        return !this.nonSummableProperties.includes(property);
    }

    updateCategoriesTranslations() {
        for (var i = 0; i < CommonCategoriesIds.length; i++) {
            var value = this.categoryIdNameDictionary[CommonCategoriesIds[i]];
            if (value != null && !this.commonCategories.includes(value))
                this.commonCategories.push(value);
        }
    }

    findByDisplayName(props, value, forgrouping = false) {
        var res = null;

        if (this.customParametersObject != null && this.customParametersObject.parameters != null && Object.keys(this.customParametersObject.parameters).includes(value)) {
            try {
                res = {};

                var modelId = modelIdToKey(this.propertiesManager.viewerIdToModelId[props.dbId]);
                if (this.customParametersObject.values[value] == null || this.customParametersObject.values[value][modelId] == null)
                    return null;


                var rawValue = this.customParametersObject.values[value][modelId];

                if (rawValue == null)
                    return null;

                if (this.customParametersObject.parameters[value].type == valueTypes.customNumber || this.customParametersObject.parameters[value].type == valueTypes.proNumber)
                    res.displayValue = parseFloat(rawValue);
                else if (this.customParametersObject.parameters[value].type == valueTypes.customDate || this.customParametersObject.parameters[value].type == valueTypes.proDate) {
                    var d = new Date(rawValue);
                    if (forgrouping) {
                        res.displayValue = d.toLocaleDateString('ja-JP', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                        });
                    }
                    else {
                        res.displayValue = d;
                    }
                }
                else
                    res.displayValue = rawValue;

                return res;
            } catch (e) {
                return null;
            }
        }

        if (value == LinkDemiParameterName) {
            res = new Object();
            res.displayValue = this.propertiesManager.GetModel(props.dbId);
            return res;
        }
        if (value == FamilyDemiParameterName) {
            res = new Object();
            var splitName = props.name.split(" [");
            res.displayValue = splitName.length > 1 ? splitName[0] : "";
            return res;
        }

        if (value == VolumeGeometry) {
            res = new Object();
            res.displayValue = getVolume(this.viewer, props.dbId) * 0.02831685; // convert from ft^3 to m^3
            res.units = knownUnits.cubicMeters;

            return res;
        }

        if (value == VolumeGeometryCm) {
            res = new Object();
            res.displayValue = getVolume(this.viewer, props.dbId) * 28316.85; // convert from ft^3 to m^3
            res.units = knownUnits.cubicCentimeters;

            return res;
        }

        if (value == SurfaceGeometry) {
            res = new Object();
            res.displayValue = getSurfaceArea(this.viewer, props.dbId) * 0.09290304; // convert from ft^2 to m^2
            res.units = knownUnits.squareMeters;

            return res;
        }

        if (value == SurfaceGeometryCm) {
            res = new Object();
            res.displayValue = getSurfaceArea(this.viewer, props.dbId) * 929.0304; // convert from ft^2 to m^2
            res.units = knownUnits.squareCentimeters;

            return res;
        }

        if (value == BimsearchLevel) {
            var foundLevelById = false;
            // try getting secret level prop to get id
            if (this.propertiesManager.GetModel(props.dbId) == this.modelName) {
                var levelProp = props.properties.find(
                    (prop) => (prop.attributeName == "Level" || prop.displayName == "Level")
                );
                if (levelProp != null && levelProp.displayValue != null && this.propertiesManager.levels[levelProp.displayValue] != null) {
                    res = Object.assign({}, levelProp);
                    res.displayValue = this.propertiesManager.levels[levelProp.displayValue];
                    foundLevelById = true;
                }
            }
            if (!foundLevelById) {
                res = tryPropNames(props, ["Reference Level", "Schedule Level", "Base Level", "Base Constraint", "Level"]);
            }
        }
        else {
            res = props.properties.find(
                (prop) => (prop.attributeName == value || prop.displayName == value) && prop.hidden == false
            );
        }

        if (res == null) res = props.properties.find((prop) => prop.attributeName == value || prop.displayName == value);

        if (res == null && value == CategoryParameterName) {
            res = new Object();
            var sourceFileName = this.findByDisplayName(props, "Source File");
            if (sourceFileName != null && sourceFileName.displayValue.toLowerCase().endsWith("dwg"))
                res.displayValue = "DWG elements";
            else
                res.displayValue = ElementsWithoutCategoryCategory;
            return res;
        }

        return res;
    }

    getMenusDataAfterSecondFilter() {
        var res = new DataForMenus();
        var sharedProperties = this.getSharedPropertiesByDisplayName(this.filteredElements);

        var counterData = this.getCounterData(this.filteredElements);

        res.quickCounterData = counterData;
        res.properties = sharedProperties;
        res.groupingOptions = sharedProperties;

        return res;
    }

    isRvt() {
        return this.modelName.toLowerCase().endsWith(".rvt");
    }

    splitCommon(resultItem) {
        if (resultItem.categories != null) {
            resultItem.commonCategories = resultItem.categories.filter(category => this.commonCategories.includes(category));
            resultItem.uncommonCategories = resultItem.categories.filter(category => !this.commonCategories.includes(category));
        }

        if (resultItem.properties != null) {

            resultItem.commonProperties = resultItem.properties.filter(property => this.commonProperties.includes(property));
            resultItem.uncommonProperties = resultItem.properties.filter(property => !this.commonProperties.includes(property));
        }

        if (resultItem.groupingOptions != null) {

            resultItem.commonGroupingOptions = resultItem.groupingOptions.filter(property => this.commonGroupingOptions.includes(property));
            resultItem.uncommonGroupingOptions = resultItem.groupingOptions.filter(property => !this.commonGroupingOptions.includes(property));
        }

        return resultItem;
    }

    async getInstancesSchedule(properties) {
        var sums = [];
        var units = [];
        var types = [];

        for (var i = 0; i < properties.length; i++) {
            sums.push(null);
            units.push(null);
            types.push(valueTypes.unknown);
        }

        var res = new DataForTable();

        for (var i = 0; i < this.filteredElements.length; i++) {
            var lineElements = [];
            lineElements.push(this.filteredElements[i]);
            var lineData = new RowInfo(lineElements);

            var props = this.propertiesManager.GetProperties(this.filteredElements[i]);

            lineData[ElementHeaderKey] = props.name;

            for (var j = 0; j < properties.length; j++) {
                var prop = this.findByDisplayName(props, properties[j]);

                var value = "";

                if (prop) {
                    if (typeof prop.displayValue == "number") {
                        var eUnits = true;
                        if (prop.units != null) eUnits = prop.units;
                        if (units[j] == null) units[j] = eUnits;
                        else if (units[j] != eUnits && prop.displayValue != 0)
                            units[j] = "Mixed units";
                    }
                    if (typeof prop.displayValue == "number" && this.isSummable(properties[j])) {
                        value = prop.displayValue;
                        sums[j] = sums[j] == null ? prop.displayValue : sums[j] + prop.displayValue;

                    } else {
                        value = prop.displayValue;
                    }
                }

                if (value != "") {
                    if (typeof value == "number") {
                        if (types[j] == valueTypes.unknown)
                            types[j] = valueTypes.number;
                    }
                    else {
                        if (value.length == 10 && dateRegex.test(value)) {
                            if (types[j] == valueTypes.unknown)
                                types[j] = valueTypes.date;
                            else if (types[j] == valueTypes.number)
                                types[j] = valueTypes.text;
                        }
                        else
                            types[j] = valueTypes.text;
                    }
                }



                lineData[properties[j]] = value;
            }
            res.rowsData.push(lineData);
        }

        res.columnsInfo.push(new ColumnInfo(ElementHeaderKey, i18next.t('element'), valueTypes.text, null));

        for (var i = 0; i < properties.length; i++) {
            var propExtension = "";
            var unit = null;
            if (sums[i] != null)
                propExtension += " Σ";

            if (units[i] != null && units[i] == "Mixed units")
                propExtension += " [Mixed units]";
            if (units[i] != null && units[i] != true && units[i] != "Mixed units")
                unit = formatUnit(units[i]);

            var totalVal = "";
            if (sums[j] != null) {
                totalVal = sums[j];
            }

            if (Object.keys(this.customParametersObject.parameters).includes(properties[i])) {
                types[i] = this.customParametersObject.parameters[properties[i]].type;
            } else {
                // Check if it's a pro parameter
                const proParamType = this.getProParameterType(properties[i]);
                if (proParamType !== null) {
                    types[i] = proParamType;
                }
            }

            res.columnsInfo.push(new ColumnInfo(properties[i], properties[i] + propExtension, types[i], unit, sums[i] != null));
        }

        return res;
    }

    addElementToDataArray(dataFieldArray, itemProps) {
        var areaParam = this.findByDisplayName(itemProps, "Area");
        var volumeParam = this.findByDisplayName(itemProps, "Volume");
        var lengthParam = this.findByDisplayName(itemProps, "Length");

        dataFieldArray[0].value++;
        addPropertyToDataField(dataFieldArray[1], lengthParam);
        addPropertyToDataField(dataFieldArray[2], areaParam);
        addPropertyToDataField(dataFieldArray[3], volumeParam);
    }

     getCommonPropertiesNames() {
    return this.commonProperties
}
}

const dateRegex = new RegExp('[0-9]{4}\/[0-9]{2}\/[0-9]{2}');
const categoryPre = "Revit ";
const LinkDemiParameterName = "Revit Link";
const FamilyDemiParameterName = "Family";
const CategoryParameterName = "Category";
const BimsearchLevel = "Level - BIMSearch";
const VolumeGeometry = "Volume [m^3] - BIMSearch";
const VolumeGeometryCm = "Volume [cm^3] - BIMSearch";
const SurfaceGeometry = "Surface Area [m^2] - BIMSearch";
const SurfaceGeometryCm = "Surface Area [cm^2] - BIMSearch";

const GroupingHeaderKey = "BIMSearch_grouping_header";
const CountHeaderKey = "BIMSearch_count_header";
const ElementHeaderKey = "BIMSearch_element_header";
const ElementsWithoutCategoryCategory = "No Category";
const NonSummableProperties = ["Diameter", "Tray Width", "Tray Height", "Thickness", "Angle", "Height", "Width", "Duct Width", "Duct Height", "H", "Railing Height", "Bottom Elevation", "Level", "ID", "Cost", "Unconnected Height", "Base Offset", "Top Offset", "Pile Diameter"];
const collectionDefaultProperties = ["Length", "Area", "Volume"];
const savedParameterNames = ["Source File", "CategoryId", "Source File"];

const getCollectionDefaultProperties = () => [
  { label: i18next.t("length"), accessor: "Length" },
  {
    label: i18next.t("area"),
    accessor: "Area",
  },
  { label: i18next.t("volume"), accessor: "Volume" },
];

const knownUnits = {
    centimeters: "cm",
    squareCentimeters: "cm^2",
    cubicCentimeters: "cm^3",
    meters: "m",
    squareMeters: "m^2",
    cubicMeters: "m^3"
};

function formatUnit(unitString) {
    if (unitString == null)
        return null;
    var res = unitString;
    if (res.startsWith("autodesk.unit")) {
        var splitUnitString = res.split(":");
        splitUnitString.shift();
        res = splitUnitString.join(":");
        splitUnitString = res.split("-");
        splitUnitString.pop();
        res = splitUnitString.join("-");

        if (Object.keys(knownUnits).includes(res))
            res = knownUnits[res];
    }

    return res;
}

function roundNumber(number) {
    return Math.round(number * 100) / 100;
}

function tryPropNames(props, names) {
    if (names == null || names.length == null)
        return null;
    var res = null;
    for (var i = 0; res == null && i < names.length; i++) {
        res = props.properties.find(
            (prop) => (prop.attributeName == names[i] || prop.displayName == names[i]));
    }

    return res;
}

const CommonCategories = ["Structural Foundations", "Walls", "Floors", "Structural Columns", "Structural Framing",
    "Ceilings", "Doors", "Windows", "Curtain Walls", "Rooms", "Pipes", "Ducts", "Duct Fittings", "Cable Trays",
    "Air Terminals", ElementsWithoutCategoryCategory];

const CommonCategoriesIds = [-2001300, -2000011, -2000032, -2001330, -2001320,
    -2000038, -2000023, -2000014, -2000160, -2008044, -2008000, -2008010, -2008130,
    -2008013];

const CommonProperties = [
  "Revit Link",
  CategoryParameterName,
  "Type Name",
  "Type Mark",
  "Mark",
  "Description",
  "Comments",
  BimsearchLevel,
  "Area",
  "Volume",
  "Length",
  "Diameter",
  "Perimeter",
  "Thickness",
  "Structural Material",
  "Layer",
  "Workset"
];

const CommonGroupingOptions = ["Type Name", "Description",
    "Diameter", "Type Mark", "Structural Material", "Thickness", "Revit Link", "System Type", "System Name", BimsearchLevel, "Layer"];

const DefaultTemplatesnFolder = "Default";



function addPropertyToDataField(dataField, property) {
    if (property != null && typeof property.displayValue == "number") {
        dataField.value += property.displayValue;
        if (property.units != null) {
            var units = formatUnit(property.units);
            if (dataField.unit == null) dataField.unit = units;
            else if (dataField.unit != units) dataField.unit = "Mixed";
        }
    }
}

// Get user Templates
async function getUserTemplates() {
    try {
        return await getFromServer("/api/forge/userdata/templates");
    }
    catch (err) {
        logError("FrontEnd.src.utils.builder.IScheduleLogic.getUserTemplates", err.toString());
        return new Object();
    }
}


function getVolume(viewer, dbId) {
    var volume = 0;
    var it = viewer.model.getData().instanceTree;
    var validCalculation = true;

    it.enumNodeFragments(dbId, function (fragId) {
        getVertices(viewer, fragId, (p1, p2, p3) => {
            var triangleVolume = getTriangleVolume(p1, p2, p3);
            if (triangleVolume == null) {
                validCalculation = false;
                return 0;
            }
            volume += triangleVolume;
        })
    }, true);

    if (validCalculation)
        return Math.abs(volume);
    else
        return 0;
}

function getSurfaceArea(viewer, dbId) {
    var area = 0;
    var it = viewer.model.getData().instanceTree;
    var validCalculation = true;

    it.enumNodeFragments(dbId, function (fragId) {
        getVertices(viewer, fragId, (p1, p2, p3) => {
            var triangleArea = getTriangleArea(p1, p2, p3);
            if (triangleArea == null) {
                validCalculation = false;
                return;
            }
            area += triangleArea;
        })
    }, true);

    return Math.abs(area);
}

function getTriangleArea(p1, p2, p3) {
    var tr = new window.THREE.Triangle(p1, p2, p3);
    var res = tr.area();
    return fixNansAndNegatives(res);
}

function getTriangleVolume(p1, p2, p3) {
    var res = p1.dot(p2.cross(p3)) / 6.0;

    return fixNansAndNegatives(res);
}

function fixNansAndNegatives(number) {
    if (Number.isFinite(number))
        return number;
    else {
        return null;

    }
}

function getVertices(viewer, fragId, callback) {
    var fragProxy = viewer.impl.getFragmentProxy(
        viewer.model,
        fragId);

    var renderProxy = viewer.impl.getRenderProxy(
        viewer.model,
        fragId);

    fragProxy.updateAnimTransform();

    var matrix = new window.THREE.Matrix4();
    fragProxy.getWorldMatrix(matrix);

    var geometry = renderProxy.geometry;

    var attributes = geometry.attributes;

    var vA = new window.THREE.Vector3();
    var vB = new window.THREE.Vector3();
    var vC = new window.THREE.Vector3();

    if (attributes.index !== undefined) {

        var indices = attributes.index.array || geometry.ib;
        var positions = geometry.vb ? geometry.vb : attributes.position.array;
        var stride = geometry.vb ? geometry.vbstride : 3;
        var offsets = geometry.offsets;

        if (!offsets || offsets.length === 0) {

            offsets = [{ start: 0, count: indices.length, index: 0 }];
        }

        for (var oi = 0, ol = offsets.length; oi < ol; ++oi) {

            var start = offsets[oi].start;
            var count = offsets[oi].count;
            var index = offsets[oi].index;

            for (var i = start, il = start + count; i < il; i += 3) {

                var a = index + indices[i];
                var b = index + indices[i + 1];
                var c = index + indices[i + 2];

                vA.fromArray(positions, a * stride);
                vB.fromArray(positions, b * stride);
                vC.fromArray(positions, c * stride);

                vA.applyMatrix4(matrix);
                vB.applyMatrix4(matrix);
                vC.applyMatrix4(matrix);

                callback(vA, vB, vC)
            }
        }
    }
    else {
        var positions = geometry.vb ? geometry.vb : attributes.position.array;
        var stride = geometry.vb ? geometry.vbstride : 3;

        for (var i = 0, j = 0, il = positions.length; i < il; i += 3, j += 9) {

            var a = i;
            var b = i + 1;
            var c = i + 2;

            vA.fromArray(positions, a * stride);
            vB.fromArray(positions, b * stride);
            vC.fromArray(positions, c * stride);

            vA.applyMatrix4(matrix);
            vB.applyMatrix4(matrix);
            vC.applyMatrix4(matrix);

            callback(vA, vB, vC)
        }
    }
}

function isSameDay(date1, date2) {
    try {
        return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    }
    catch {
        return false;
    }
}
